<template>
  <div class="fts-classification" :data-sync="domSyncState" :data-form-load="isFormLoading ? 'loading' : 'loaded'">
    <!-- §4.3.3 form-level GNey actions: designer-configured, user-invoked -->
    <div
      class="teammate-form-actions"
      v-if="!view && !viewData && teammateFormActions.length"
    >
      <fts-btn
        sm
        outline
        text-transform-init
        :disabled="teammateFillPending"
        v-for="action in teammateFormActions"
        :key="action.id"
        @click="runTeammateAction(action)"
      >
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
        {{ action.label || 'GNey' }}
      </fts-btn>
    </div>

    <!-- §4.3.1 live-form GNey fill status (pending / filled+Undo / quiet error) -->
    <fts-teammate-fill-strip v-if="!view && !viewData" />

    <fts-form
      :vector-id="vectorId"
      :activity-id="activityId"
      :form="form"
      :form-data="formData"
      :validator="$v.formData"
      :context="context"
      :view="view"
      :share-form="shareForm"
      :username="username"
      :comfort-design="comfortDesign"
      :form-settings="formSettings"
      :form-element-overflow-auto="formElementOverflowAuto"
      :form-element-disabled="isFormLoading"
      :design-mode="!loading && isCompatibleDesignMode && !isFallbackDesignMode"
      :shortcut-options="availableInformation"
      :shortcut-selected-options="classificationInformation"
      :shortcut-elements="shortcutElements"
      :without-information="isFormWithoutInformation"
      @eventTriggered="triggerEvent"
      @design:change="updateDesign"
      v-if="!comment.attention"
    />

    <div
      class="form-hint-group form-hint-action-wrapper"
      v-if="!loading && isCompatibleDesignMode && isFallbackDesignMode"
    >
      <router-link :to="designRoute" target="_blank" class="form-hint-action">
        <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" fixed-width class="form-hint-action-icon" />Click to design form
      </router-link>
    </div>

    <fts-comment
      :vector-id="vectorId"
      :comment="comment"
      :mention="commentMention"
      class="classification-comment"
      v-if="commentSection && !loading && !view"
    />
  </div>
</template>
<script>
import Vue from 'vue';
import Vuex from 'vuex';
Vue.use(Vuex);
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);

import { classificationStore } from '../store/classificationStore';
import { clientStore } from '../store/clientStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import canvasDesign from '../commons/canvasDesign';
import commons from '../commons/commons';
import formDesign from '../commons/formDesign';

import FTSBtn from './FTSBtn';
import FTSComment from './FTSComment';
import FTSForm from './FTSForm';
import FTSFormLayouter from '../design/FTSFormLayouter';
import FTSTeammateFillStrip from './FTSTeammateFillStrip';

export default {
  props: {
    locusId: [String, Number],
    locusIds: String,
    vectorId: [String, Number],
    activityId: String,
    activity: String,
    users: Array,
    currentUsername: String,
    view: Boolean,
    shareForm: Boolean,
    loading: Boolean,
    submitting: Boolean,
    synchronizing: Boolean,
    context: Object,
    username: String,
    viewData: Object,
    comfortDesign: Boolean,
    formElementOverflowAuto: Boolean,
    formValidationError: Function,
    designMode: Boolean,
    formSettings: Object,
    /* classification section */
    commentSection: {
      type: Boolean,
      default: true,
    },
    comment: {
      type: Object,
      default: () => ({}),
    },
    commentMention: {
      type: Boolean,
      default: true,
    }
  },
  data: () => ({
    polling: null,
    selectedActivity: null,
    formChanged: false
  }),
  validations() {
    let formData = {};
    let elements = commons.getVisibleElements(null, this.form);
    /* temp for visibilityScript */
    elements = elements.filter(x => commons.formElementVisibility(x, this.formData));
    /* end */
    formData = commons.getFormValidators(elements, null, this.formData);
    return { formData };
  },
  store: classificationStore,
  components: {
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-form": FTSForm,
    "fts-teammate-fill-strip": FTSTeammateFillStrip
  },
  computed: {
    formData: {
      get: function () {
        if(this.viewData)
          return this.viewData.formData;
        return this.$store.state.formData;
      },
    },
    form: {
      get: function () {
        if(this.viewData)
          return this.viewData.form;
        return this.$store.state.form;
      },
    },
    sync() {
      return this.$store.state.needSync;
    },
    syncState() {
      if (this.$store.state.needSync && !this.viewData) {
        if (this.$store.state.synchronizing)
          return "syncing";
        else if (this.$store.state.syncError)
          return "resync";
        else
          return "synced";
      }
    },
    /* What the data-sync hook reports. A control-only form is fullyClientComputable under native
       bundle v4, so it takes the zero-sync fast path and never sets needSync -- syncState then goes
       undefined, Vue drops the attribute, and anything waiting on it waits forever. The form has in
       fact settled (controls are evaluated locally), so report that.

       syncState itself stays needSync-only: submit() and isFallbackDesignMode branch on it, and a
       fully-client-computable form must not be pushed through synchronize() on submit nor dropped
       into fallback design mode. This is the presentational mirror, nothing more. */
    domSyncState() {
      if (this.syncState)
        return this.syncState;

      const nativeModel = this.$store.state.nativeModel;
      if (nativeModel && nativeModel.fullyClientComputable && !this.viewData)
        return this.$store.state.synchronizing ? "syncing" : "synced";
    },
    isFormLoading() {
      const synchronizing = this.$store.state.synchronizing;
      this.$emit("update:synchronizing", synchronizing);
      return synchronizing;
    },
    designRoute() {
      return { name: 'studio', query: { id: commons.encodeids(this.vectorId)}};
    },
    isCompatibleDesignMode() {
      if (!this.designMode || this.activityId == "manage")
        return false;

      return true;
    },
    isFallbackDesignMode() {
      if (!!this.syncState)
        return true;

      return this.form && this.form.rows.some(row =>
        row.items && row.items.some(item =>
          item.type == 15 || ['section', 'table', 'list', 'empty', 'button', 'custom'].includes(item.input)
        )
      );
    },
    isFormWithoutInformation() {
      return this.form && this.form.rows ? !formDesign.isFormContainsInformation(this.form.rows) : true;
    },
    canvasInformation() {
      if (this.designMode)
        return canvasDesignStore.getters.canvasInformation;
    },
    classificationInformation() {
      if (this.designMode && this.form && this.form.rows)
        return commons.classificationInformation(this.form.rows, this.canvasInformation);
    },
    availableInformation() {
      if (this.designMode)
        return [...this.canvasInformation].filter((x) =>
          !this.classificationInformation.some((y) => y.id == x.id)
        );
    },
    shortcutElements() {
      return [formDesign.Element.Content, formDesign.Element.Image];
    },
    /* §4.3.3 form-level GNey actions from the design (enabled !== false = enabled) */
    teammateFormActions() {
      const actions = this.form && this.form.aiActions;
      if (!Array.isArray(actions)) return [];
      return actions.filter((x) => x && x.id && x.enabled !== false);
    },
    teammateFillPending() {
      const fill = this.$store.state.teammateFill;
      return !!(fill && fill.status === "pending");
    }
  },
  methods: {
    /* §4.3.3: the click IS the invocation — same store pipeline as a sync directive.
       selectedActivity is only populated by pollData(), which the host views call solely
       when the form needs sync — so on a non-syncing form it is null. Fall back to the
       activityId prop (the fill endpoint only needs the id). */
    runTeammateAction(action) {
      const activity =
        this.selectedActivity || (this.activityId ? { id: this.activityId } : null);
      if (!activity) return;
      this.$store.dispatch("teammateFillRun", {
        directive: {
          nodeId: "$form",
          actionId: action.id,
          label: action.label || "GNey",
        },
        activity: activity,
      });
    },
    triggerEvent({ eventName, eventPayload }) {
      classificationStore.dispatch('eventTriggered', {
        activity: this.selectedActivity,
        eventName: eventName,
        eventPayload: eventPayload
      });
    },
    addToForm(cartItems) {
      if (this.formData[cartItems.fields[0].fieldName] == null) {
        Vue.set(this.formData, cartItems.fields[0].fieldName, []);
      }
      //find form field,
      let rows = this.formData[cartItems.fields[0].fieldName];
      cartItems.items.forEach((item) => {
        var row = {
          id: commons.newUUID(),
        };

        let fieldName = cartItems.fields[1].fieldName;
        let connectionFieldName = cartItems.fields[1].connectionFieldName;
        Vue.set(row, fieldName + "Id", item.id);
        Vue.set(row, fieldName, item.extension[connectionFieldName]);

        rows.push(row);
      });
    },
    async submit() {
      this.$emit("update:submitting", true);
      if (this.syncState) {
        await this.synchronize({});
      }
      this.$v.$touch();
      if (this.designMode && this.activityId === 'create' && this.isFormWithoutInformation) {
        this.$toasted.error('Before submitting the form, you will need to add at least one field');
        return;
      }
      if (this.$v.$invalid) {
        this.$emit("update:submitting", false);
        if (this.formValidationError) {
          this.formValidationError();
        } else {
          this.$toasted.global.invalidFormValidate();
        }
      } else {
        if (this.form && this.form.bds) {
          try {
            /* SS05827: a Before Done Script whose job is to trigger ANOTHER activity does so
               asynchronously. eval returned as soon as that call had been STARTED, so the submit
               below raced it. Both writes landed, and because the server rewrites the whole entry
               from the snapshot it loaded, whichever finished last won - the script's update
               appeared in history but not in the saved entry.

               Await the script when it hands back a thenable, so it can opt into finishing first.
               A script that returns nothing behaves exactly as before, so this is additive: it
               makes "run this to completion, then submit" expressible, where before it was not.

               Note this also means an async FAILURE inside the script now surfaces here and
               aborts the submit, instead of being an unhandled rejection while the submit went
               ahead regardless. That is the intended behaviour for a pre-submit step.

               The underlying last-write-wins exposure is SS05719. */
            const beforeDoneResult = eval(this.form.bds);
            if (beforeDoneResult && typeof beforeDoneResult.then === "function") {
              await beforeDoneResult;
            }
          } catch (ex) {
            this.$toasted.error(ex);
            throw ex;
          }
        }
        this.$emit("submit", this.formData);
      }
    },
    async synchronize(payload) {
      payload.view = this.view;
      payload.polling = this.polling;
      payload.activity = this.selectedActivity;
      await this.$store.dispatch("synchronize", payload);
    },
    pollData(selectedActivity) {
      this.selectedActivity = selectedActivity;
      console.log("poll...");
      let vm = this;
      if (vm.polling) {
        console.log("clr poll" + vm.polling);
        clearInterval(vm.polling);
      }

      vm.polling = setInterval(() => {
        if (!vm.$store.getters.syncError) vm.synchronize({});
      }, 1500);
      this.$store.dispatch("polling", {
        polling: vm.polling,
      });
      console.log("crt poll: " + vm.polling);
    },
    onFormChange: _.debounce(async (vm) => {
      const nativeModel = vm.$store.state.nativeModel;
      if (vm.$store.state.appliedChange) {
        vm.$store.dispatch("resetApplyChange");
      } else if (vm.sync || (nativeModel && nativeModel.fullyClientComputable)) {
        /* recompute on every edit when syncing OR when computing natively on the client
           (needSync is false in the fully-client-computable case, so vm.sync alone misses it) */
        await vm.synchronize({});
      }
    }, 500),
    reset() {
      this.$v.$reset();
      classificationStore.dispatch('setFormData', {});
    },
    async saveDesign() {
      try {
        clientStore.dispatch("setDesignModeLoading", true);
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        let activity = null;
        if (['create', 'edit', 'view', 'quickView'].indexOf(this.activityId) > -1) {
          activity = canvas.standardActivities.find((x) => x.id === this.activityId);
        } else {
          activity = canvas.activities.find((x) => x.id === this.activityId);
        }

        if (activity) {
          if (!activity.design) {
            activity.design = {};
          }
          activity.design.rows = _.cloneDeep(this.form.rows);
          commons.compactClassification(canvas);
          canvasDesign.compactDesignBeforeSave(canvas);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          const moduleResponse = await canvasDesignStore.dispatch("saveVector", {
            activityId: this.activityId
          });
          clientStore.dispatch("setDesignModeState", {
            success: true,
            updatedOn: moduleResponse.data.updatedDate
          });
        }
      } catch(err) {
        console.error(err);
        clientStore.dispatch("setDesignModeState", { error: true });
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to save design changes'
        });
      } finally {
        clientStore.dispatch("setDesignModeLoading", false);
      }
    },
    updateDesign: _.debounce(async function() {
      await this.saveDesign();
    }, 800)
  },
  watch: {
    formData: {
      deep: true,
      handler() {
        this.formChanged = true;
        this.onFormChange(this);
      },
    },
    users() {
      this.$store.dispatch("setUsers", { users: this.users });
    }
  },
  beforeCreate() {
    Vue.component("fts-form-layouter", FTSFormLayouter);
    Vue.component("fts-form", FTSForm);
  },
  created() {
    this.$set(this.comment, "attention", false);
  },
  mounted() {
    this.$nextTick(() => {
      this.$v.$reset();
    });
  },
  beforeDestroy() {
    clearInterval(this.polling);
  }
};
</script>

<style lang="scss">
.fts-classification {
  .classification-comment {
    margin-top: 0.75rem;
  }
  .teammate-form-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin: 0.25rem 0 0.5rem;
  }
}
</style>
